import { css } from '@emotion/react';

const getConfidenceBadgeStyles = (confidenceLevel) => {
  switch (confidenceLevel) {
    case 'fair':
      return {
        backgroundColor: 'rgba(255, 235, 204, 1)',
        borderColor: 'rgba(245, 215, 153, 1)',
        textColor: 'rgba(102,62,0,1)',
        dotColor: 'rgba(245, 154, 0, 1)',
      };
    case 'medium':
      return {
        backgroundColor: 'rgba(227, 240, 219, 1)',
        borderColor: 'rgba(199, 225, 183, 1)',
        textColor: 'rgba(69, 108, 45, 1)',
        dotColor: 'rgba(115, 180, 75, 1)',
      };
    case 'high':
      return {
        backgroundColor: 'rgba(69, 108, 45, 1)',
        borderColor: 'rgba(51, 81, 33, 1)',
        textColor: 'rgba(255, 255, 255, 1)',
        dotColor: 'rgba(115, 180, 75, 1)',
      };
    default:
      return {
        backgroundColor: 'rgba(255, 255, 255, 1)',
        borderColor: 'rgba(239, 238, 239, 1)',
        textColor: 'rgba(86, 86, 96, 1)',
        dotColor: 'rgba(156, 163, 175, 1)',
      };
  }
};

const makeSmartMatchMeterStyling = ({ theme, confidenceLevel }) => {
  const borderColor = theme.mvf.palette.divider || 'rgba(0, 0, 0, 0.12)';
  const badgeStyles = getConfidenceBadgeStyles(confidenceLevel);

  return {
    container: css({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      padding: '8px 16px',
      opacity: 0,
      transition: '500ms ease opacity',
    }),

    card: css({
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.mvf.palette.background?.paper || '#ffffff',
      border: `1px solid ${borderColor}`,
      borderRadius: '8px',
      padding: '12px',
      gap: '8px',
      boxShadow: '0px 4px 8px 0 rgba(0, 0, 0, 0.05)',
    }),

    iconContainer: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '32px',
      backgroundColor: '#36363D',
      flexShrink: 0,
    }),

    label: css({
      fontSize: '0.75rem',
      fontWeight: 800,
      color: theme.mvf.palette.text.secondary || '#565660',
      fontFamily: theme.mvf.typography.fontFamily,
      letterSpacing: '0',
      lineHeight: '100%',
      margin: 0,
    }),

    statsContainer: css({
      display: 'flex',
      alignItems: 'flex-start',
      gap: '20px',
      marginLeft: 'auto',
    }),

    statItem: css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
    }),

    statLabel: css({
      fontSize: '0.75rem',
      fontWeight: '400',
      color: theme.mvf.palette.text.secondary || '#9CA3AF',
      fontFamily: theme.mvf.typography.fontFamily,
      lineHeight: 1,
      margin: 0,
    }),

    matchesValue: css({
      fontSize: '14px',
      fontWeight: '600',
      color: theme.mvf.palette.text.primary,
      fontFamily: theme.mvf.typography.fontFamily,
      lineHeight: 1,
      margin: 0,
      padding: '4px 12px',
      border: `1px solid ${borderColor}`,
      borderRadius: '20px',
    }),

    infoContainer: css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      position: 'relative',
      cursor: 'pointer',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: '50%',
        cursor: 'pointer',
        transform: 'translate(-50%, -50%)',
        width: '44px',
        height: '44px',
      },
    }),

    confidenceContainer: css({
      display: 'flex',
      alignItems: 'center',
    }),

    confidenceBadge: css({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '8px',
      padding: '4px 12px',
      borderRadius: '20px',
      backgroundColor: badgeStyles.backgroundColor,
      border: `1px solid ${badgeStyles.borderColor}`,
    }),

    confidenceDot: css({
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: badgeStyles.dotColor,
    }),

    confidenceText: css({
      fontSize: '14px',
      fontWeight: '600',
      color: badgeStyles.textColor,
      fontFamily: theme.mvf.typography.fontFamily,
      lineHeight: 1,
      margin: 0,
      textTransform: 'capitalize',
    }),

    popoverPaper: {
      borderRadius: '8px',
      boxShadow: 'none',
      marginTop: '12px',
      maxWidth: '400px',
      width: 'calc(100vw - 32px)',
      overflow: 'visible',
      filter: 'drop-shadow(0px 8px 24px rgba(0, 0, 0, 0.2))',
    },

    popoverArrow: css({
      position: 'absolute',
      top: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: `8px solid ${
        theme.mvf.palette.background?.paper || '#ffffff'
      }`,
    }),

    popoverContent: css({
      padding: '16px',
      fontSize: '0.875rem',
      fontWeight: theme.mvf.typography.weight.regular,
      color: theme.mvf.palette.text.primary,
      fontFamily: theme.mvf.typography.fontFamily,
      lineHeight: 1.5,
      margin: 0,
    }),
  };
};

export default makeSmartMatchMeterStyling;
